/**
 * Browser-Use admin console — submit + monitor browser-automation jobs.
 *
 * Marina and Bitsy can trigger jobs programmatically; this page is the
 * human ops surface for: ad-hoc tasks, vendor-credential vault, live
 * progress, audit trail.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Play, Globe, Key, Trash2, CheckCircle2, XCircle,
  Clock, AlertTriangle, ChevronDown, ChevronRight, Plus, X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { API } from '@/lib/backendUrl';
import SkillsPanel from '@/pages/BrowserUseSkillsPanel';

const STATUS_COLORS = {
  queued: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30',
  running: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  completed: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  failed: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  cancelled: 'bg-zinc-700/30 text-zinc-400 border-zinc-700/50',
  blocked_by_critic: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
};
const STATUS_ICON = {
  completed: <CheckCircle2 className="w-4 h-4 text-emerald-400" />,
  failed: <XCircle className="w-4 h-4 text-rose-400" />,
  running: <Clock className="w-4 h-4 text-blue-400 animate-pulse" />,
  queued: <Clock className="w-4 h-4 text-zinc-500" />,
  blocked_by_critic: <AlertTriangle className="w-4 h-4 text-amber-400" />,
  cancelled: <X className="w-4 h-4 text-zinc-500" />,
};

const SUGGESTED_PRESETS = [
  { label: 'Fetch H1 from example.com', task: 'Open https://example.com and report the H1 text on the page.' },
  { label: 'Booking.com rate for tomorrow (Tokyo)', task: 'Open https://www.booking.com, search for hotels in Tokyo for tomorrow night, and report the top 3 hotel names with their lowest visible price.' },
  { label: 'OpenTable: find dinner reservation', task: 'Open https://www.opentable.com, search for an Italian restaurant in New York for 2 people at 7:30pm tomorrow, and report the top 3 available restaurants with their reservation times.' },
];

function fmtRelative(iso) {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function BrowserUseConsolePage() {
  const nav = useNavigate();
  const [tab, setTab] = useState('jobs'); // 'jobs' | 'skills' | 'credentials'
  const [health, setHealth] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);

  // submit form
  const [task, setTask] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [maxSteps, setMaxSteps] = useState(15);
  const [submitting, setSubmitting] = useState(false);

  // credential form
  const [showCredForm, setShowCredForm] = useState(false);
  const [credVendor, setCredVendor] = useState('');
  const [credLabel, setCredLabel] = useState('');
  const [credFields, setCredFields] = useState([{ k: 'username', v: '' }, { k: 'password', v: '' }]);

  // ─── load ───────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [hRes, jRes, cRes] = await Promise.all([
        fetch(`${API}/api/browser-use/health`),
        fetch(`${API}/api/browser-use/jobs?limit=100`),
        fetch(`${API}/api/browser-use/credentials`),
      ]);
      setHealth(await hRes.json());
      setJobs((await jRes.json()).jobs || []);
      setCredentials((await cRes.json()).credentials || []);
    } catch {
      // keep prior state on transient failure
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  // Poll while jobs are running
  useEffect(() => {
    const hasActive = jobs.some((j) => j.status === 'running' || j.status === 'queued');
    if (!hasActive) return undefined;
    const iv = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, 4000);
    return () => clearInterval(iv);
  }, [jobs, load]);

  // ─── actions ────────────────────────────────────────────────
  const submitJob = async () => {
    if (!task.trim() || task.trim().length < 10) {
      setActionMsg({ type: 'err', text: 'task must be at least 10 chars' });
      return;
    }
    setSubmitting(true);
    setActionMsg(null);
    try {
      const r = await fetch(`${API}/api/browser-use/jobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          task: task.trim(),
          target_url: targetUrl.trim() || null,
          initiator: 'admin-console',
          max_steps: Number(maxSteps) || 15,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'submit failed');
      setActionMsg({ type: 'ok', text: `Job ${j.id} queued · status ${j.status}` });
      setTask('');
      setTargetUrl('');
      await load();
    } catch (e) {
      setActionMsg({ type: 'err', text: e.message });
    }
    setSubmitting(false);
  };

  const cancelJob = async (id) => {
    try {
      await fetch(`${API}/api/browser-use/jobs/${id}/cancel`, { method: 'POST' });
      await load();
    } catch {}
  };

  const submitCredential = async () => {
    if (!credVendor.trim() || !credLabel.trim()) {
      setActionMsg({ type: 'err', text: 'vendor and label required' });
      return;
    }
    const fields = Object.fromEntries(credFields.filter((f) => f.k && f.v).map((f) => [f.k, f.v]));
    try {
      const r = await fetch(`${API}/api/browser-use/credentials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vendor: credVendor.trim(),
          label: credLabel.trim(),
          fields,
          created_by: 'admin-console',
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'save failed');
      setActionMsg({ type: 'ok', text: `Stored ${credVendor}/${credLabel}` });
      setShowCredForm(false);
      setCredVendor(''); setCredLabel('');
      setCredFields([{ k: 'username', v: '' }, { k: 'password', v: '' }]);
      await load();
    } catch (e) {
      setActionMsg({ type: 'err', text: e.message });
    }
  };

  const deleteCredential = async (vendor, label) => {
    if (!window.confirm(`Delete credentials for ${vendor}/${label}?`)) return;
    try {
      await fetch(`${API}/api/browser-use/credentials/${encodeURIComponent(vendor)}/${encodeURIComponent(label)}`, { method: 'DELETE' });
      await load();
    } catch {}
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-zinc-100">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => nav('/admin')} data-testid="bu-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Admin
            </Button>
            <div>
              <h1 className="text-2xl font-semibold flex items-center gap-2">
                <Globe className="w-6 h-6 text-amber-400" />
                Browser Automation Console
              </h1>
              <p className="text-xs text-zinc-400">
                Marina &amp; Bitsy use this to drive vendor portals · Critic-gated · audit-logged
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={load} data-testid="bu-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Health */}
        <Card className="bg-zinc-900/60 border-zinc-800 mb-5">
          <CardContent className="p-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <Globe className={`w-4 h-4 ${health?.any_provider_ready ? 'text-emerald-400' : 'text-rose-400'}`} />
                <span className="text-sm font-medium">
                  {health?.any_provider_ready
                    ? `LLM ready · ${health.provider} / ${health.model}`
                    : 'NO LLM provider configured — set BROWSER_USE_OPENAI_API_KEY or _ANTHROPIC_API_KEY in /app/backend/.env'}
                </span>
              </div>
              <div className="flex items-center gap-2 text-[11px]">
                {['openai', 'anthropic', 'google'].map((p) => (
                  <Badge key={p} className={health?.[`${p}_ready`] ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' : 'bg-zinc-800 text-zinc-500 border-zinc-700'}>
                    {p} {health?.[`${p}_ready`] ? '✓' : '—'}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => setTab('jobs')}
            className={`px-3 py-1.5 rounded-full text-sm border ${tab === 'jobs' ? 'bg-amber-500/20 border-amber-500/50 text-amber-200' : 'border-zinc-800 text-zinc-400 hover:bg-zinc-900'}`}
            data-testid="bu-tab-jobs-btn"
          >
            Jobs
          </button>
          <button
            onClick={() => setTab('skills')}
            className={`px-3 py-1.5 rounded-full text-sm border ${tab === 'skills' ? 'bg-amber-500/20 border-amber-500/50 text-amber-200' : 'border-zinc-800 text-zinc-400 hover:bg-zinc-900'}`}
            data-testid="bu-tab-skills-btn"
          >
            Skills Library
          </button>
          <button
            onClick={() => setTab('credentials')}
            className={`px-3 py-1.5 rounded-full text-sm border ${tab === 'credentials' ? 'bg-amber-500/20 border-amber-500/50 text-amber-200' : 'border-zinc-800 text-zinc-400 hover:bg-zinc-900'}`}
            data-testid="bu-tab-creds-btn"
          >
            Vendor credentials
          </button>
        </div>

        {actionMsg && (
          <div
            className={`mb-3 px-3 py-2 rounded text-sm ${
              actionMsg.type === 'ok'
                ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
                : 'bg-rose-500/10 text-rose-300 border border-rose-500/30'
            }`}
            data-testid="bu-action-msg"
          >
            {actionMsg.text}
          </div>
        )}

        {tab === 'jobs' && (
          <>
            {/* Submit form */}
            <Card className="bg-zinc-900/60 border-zinc-800 mb-5">
              <CardContent className="p-4">
                <div className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Play className="w-4 h-4 text-amber-400" /> Submit a new task
                </div>
                <div className="flex flex-wrap gap-1 mb-2">
                  {SUGGESTED_PRESETS.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => { setTask(p.task); setTargetUrl(''); }}
                      className="text-[11px] px-2 py-1 rounded-full border border-zinc-800 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
                      data-testid={`bu-preset-${i}-btn`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <textarea
                  value={task}
                  onChange={(e) => setTask(e.target.value)}
                  placeholder="Describe in plain English what the browser should do…"
                  className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm min-h-[80px]"
                  data-testid="bu-task-input"
                />
                <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_140px] gap-2 mt-2">
                  <input
                    type="text"
                    value={targetUrl}
                    onChange={(e) => setTargetUrl(e.target.value)}
                    placeholder="target URL hint (optional, also fed to Critic)"
                    className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm"
                    data-testid="bu-target-url-input"
                  />
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={maxSteps}
                    onChange={(e) => setMaxSteps(e.target.value)}
                    placeholder="max steps"
                    className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm"
                    data-testid="bu-max-steps-input"
                  />
                  <Button
                    onClick={submitJob}
                    disabled={submitting || !health?.any_provider_ready}
                    className="bg-amber-600 hover:bg-amber-500"
                    data-testid="bu-submit-btn"
                  >
                    {submitting ? 'Submitting…' : 'Run task'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Jobs list */}
            {loading ? (
              <div className="text-zinc-500 py-12 text-center">Loading…</div>
            ) : jobs.length === 0 ? (
              <Card className="bg-zinc-900/40 border-zinc-800">
                <CardContent className="py-10 text-center text-zinc-500">
                  No jobs yet. Submit one above.
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {jobs.map((j) => (
                  <JobRow
                    key={j.id}
                    job={j}
                    open={openId === j.id}
                    onToggle={() => setOpenId(openId === j.id ? null : j.id)}
                    onCancel={() => cancelJob(j.id)}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {tab === 'skills' && (
          <SkillsPanel
            credentials={credentials}
            onJobSubmitted={(j) => {
              setActionMsg({ type: 'ok', text: `Skill run ${j.id} queued · status ${j.status}` });
              setTab('jobs');
              load();
            }}
            onError={(msg) => setActionMsg({ type: 'err', text: msg })}
          />
        )}

        {tab === 'credentials' && (
          <>
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs text-zinc-500">
                Vendor passwords for portals Marina/Bitsy needs to log into. Stored Fernet-encrypted. Never returned over the API in plaintext.
              </div>
              <Button size="sm" onClick={() => setShowCredForm(true)} className="bg-amber-600 hover:bg-amber-500" data-testid="bu-add-cred-btn">
                <Plus className="w-4 h-4 mr-1" /> Add credential
              </Button>
            </div>

            {showCredForm && (
              <Card className="bg-zinc-900/60 border-zinc-800 mb-4">
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                    <input
                      type="text"
                      value={credVendor}
                      onChange={(e) => setCredVendor(e.target.value)}
                      placeholder="vendor (e.g. 'bryte', 'salto', 'booking.com')"
                      className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm"
                      data-testid="bu-cred-vendor-input"
                    />
                    <input
                      type="text"
                      value={credLabel}
                      onChange={(e) => setCredLabel(e.target.value)}
                      placeholder="label (e.g. 'hotel_grand_marina_admin')"
                      className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm"
                      data-testid="bu-cred-label-input"
                    />
                  </div>
                  <div className="space-y-1">
                    {credFields.map((f, i) => (
                      <div key={i} className="grid grid-cols-[140px_1fr_36px] gap-2">
                        <input
                          type="text"
                          value={f.k}
                          onChange={(e) => setCredFields(credFields.map((x, j) => j === i ? { ...x, k: e.target.value } : x))}
                          placeholder="field name"
                          className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-xs"
                          data-testid={`bu-cred-field-key-${i}`}
                        />
                        <input
                          type="password"
                          value={f.v}
                          onChange={(e) => setCredFields(credFields.map((x, j) => j === i ? { ...x, v: e.target.value } : x))}
                          placeholder="value (encrypted at rest)"
                          className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-xs font-mono"
                          data-testid={`bu-cred-field-val-${i}`}
                        />
                        <button
                          onClick={() => setCredFields(credFields.filter((_, j) => j !== i))}
                          className="text-zinc-500 hover:text-rose-400"
                          data-testid={`bu-cred-field-del-${i}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => setCredFields([...credFields, { k: '', v: '' }])}
                      className="text-[11px] text-amber-300 hover:text-amber-200 mt-1"
                      data-testid="bu-cred-add-field-btn"
                    >
                      + add field
                    </button>
                  </div>
                  <div className="flex justify-end gap-2 mt-3">
                    <Button variant="outline" size="sm" onClick={() => setShowCredForm(false)} data-testid="bu-cred-cancel-btn">Cancel</Button>
                    <Button size="sm" onClick={submitCredential} className="bg-emerald-600 hover:bg-emerald-500" data-testid="bu-cred-save-btn">
                      Save (encrypted)
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {credentials.length === 0 ? (
              <Card className="bg-zinc-900/40 border-zinc-800">
                <CardContent className="py-10 text-center text-zinc-500">
                  No vendor credentials yet.
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {credentials.map((c) => (
                  <Card key={`${c.vendor}/${c.label}`} className="bg-zinc-900/60 border-zinc-800" data-testid={`bu-cred-row-${c.vendor}-${c.label}`}>
                    <CardContent className="p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Key className="w-4 h-4 text-amber-400" />
                        <div>
                          <div className="text-sm">
                            <span className="font-mono text-amber-300">{c.vendor}</span>
                            <span className="text-zinc-500 mx-2">·</span>
                            <span className="text-zinc-200">{c.label}</span>
                          </div>
                          <div className="text-[11px] text-zinc-500">
                            fields: {(c.field_keys || []).join(', ')}
                            {c.last_used_at && <> · last used {fmtRelative(c.last_used_at)}</>}
                            <> · created {fmtRelative(c.created_at)}</>
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteCredential(c.vendor, c.label)}
                        className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                        data-testid={`bu-cred-delete-${c.vendor}-${c.label}-btn`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> Delete
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function JobRow({ job, open, onToggle, onCancel }) {
  const canCancel = job.status === 'queued' || job.status === 'running';
  return (
    <Card className="bg-zinc-900/60 border-zinc-800" data-testid={`bu-job-row-${job.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <button onClick={onToggle} className="text-zinc-500 hover:text-zinc-200 mt-1" data-testid={`bu-job-toggle-${job.id}-btn`}>
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              {STATUS_ICON[job.status]}
              <Badge className={STATUS_COLORS[job.status] || ''}>{job.status}</Badge>
              {job.critic_review?.verdict === 'flag' && (
                <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30" title={job.critic_review.reasons?.join('\n')}>
                  <AlertTriangle className="w-3 h-3 mr-1" /> critic flagged
                </Badge>
              )}
              <Badge className="bg-zinc-800 text-zinc-300 border-zinc-700">{job.initiator}</Badge>
              <Badge className="bg-zinc-800 text-zinc-300 border-zinc-700">{(job.steps?.length || 0)}/{job.max_steps} steps</Badge>
            </div>
            <div className="text-sm text-zinc-200 line-clamp-2">{job.task}</div>
            <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-3 flex-wrap">
              <span>id <span className="font-mono">{job.id}</span></span>
              <span>· {fmtRelative(job.created_at)}</span>
              {job.target_url && <span>· target {job.target_url}</span>}
            </div>
            {job.final_result && (
              <div className="mt-2 bg-emerald-500/5 border border-emerald-500/20 rounded p-2 text-xs text-emerald-200">
                {String(job.final_result).slice(0, 400)}
                {String(job.final_result).length > 400 && '…'}
              </div>
            )}
            {job.error && (
              <div className="mt-2 bg-rose-500/5 border border-rose-500/20 rounded p-2 text-xs text-rose-200">
                {job.error}
              </div>
            )}
          </div>
          <div>
            {canCancel && (
              <Button size="sm" variant="outline" onClick={onCancel} className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10" data-testid={`bu-job-cancel-${job.id}-btn`}>
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
            )}
          </div>
        </div>

        {open && (
          <div className="mt-3 pt-3 border-t border-zinc-800/80 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <div>
              <div className="text-zinc-500 mb-1">Critic review</div>
              <div className="bg-black/40 p-2 rounded">
                <Badge className={job.critic_review?.verdict === 'pass' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}>
                  {job.critic_review?.verdict || 'n/a'}
                </Badge>
                {job.critic_review?.reasons && (
                  <ul className="mt-1 list-disc list-inside text-zinc-400">
                    {job.critic_review.reasons.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                )}
              </div>
              {job.credentials_ref && (
                <div className="mt-2 text-zinc-500">
                  using credentials: <span className="font-mono text-amber-300">{job.credentials_ref.vendor}/{job.credentials_ref.label}</span>
                </div>
              )}
            </div>
            <div>
              <div className="text-zinc-500 mb-1">Step trajectory</div>
              <div className="bg-black/40 p-2 rounded max-h-40 overflow-auto space-y-0.5">
                {(job.steps || []).length === 0 ? (
                  <div className="text-zinc-600 italic">no steps yet</div>
                ) : (
                  (job.steps || []).map((s, i) => (
                    <div key={i} className="text-zinc-400">
                      <span className="text-amber-300 font-mono">#{s.step}</span>{' '}
                      {s.url && <span className="text-zinc-300">{s.url}</span>}
                      <span className="text-zinc-600 text-[10px] ml-2">{new Date(s.ts).toLocaleTimeString()}</span>
                    </div>
                  ))
                )}
              </div>
              {job.traceback && (
                <details className="mt-2">
                  <summary className="text-rose-400 text-xs cursor-pointer">traceback</summary>
                  <pre className="bg-black/40 p-2 rounded text-[10px] text-rose-200 max-h-32 overflow-auto">{job.traceback}</pre>
                </details>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
